import { Component, Input, OnInit } from '@angular/core';
import { IonNav } from '@ionic/angular';
import { TrustPaymentPage } from '../trust-payment.page';
import { TrustPaymentDigitalWalletPage } from '../trust-payment-digital-wallet/trust-payment-digital-wallet.page';

interface PaymentMethod {
  title: string;
  type: string;
  icon: string;
  src?: string;
}

@Component({
  selector: 'app-trust-payment-method',
  templateUrl: './trust-payment-method.page.html',
  styleUrls: ['./trust-payment-method.page.scss'],
})
export class TrustPaymentMethodPage implements OnInit {
  @Input() caseId;
  @Input() totalOutstandingWithSymbol;
  @Input() caseDetails;
  @Input() debtorDetails;
  @Input() amount;
  @Input() responseData;

  paymentMethods: PaymentMethod[] = [
    {
      title: 'Card Payment',
      type: 'card',
      icon: 'card-outline',
    },
    {
      title: 'Google Pay',
      type: 'googlepay',
      icon: 'logo-google',
    },
    {
      title: 'Apple Pay',
      type: 'applepay',
      icon: 'logo-apple',
    },
    {
      title: 'Pay By Bank',
      type: 'paybybank',
      icon: 'card-outline',
      src: 'assets/icon/bank2.svg',
    },
  ];

  constructor(private ionNav: IonNav) {}

  /**
   * Lifecycle hook that is called when the component is initialized.
   * It reads the portal settings from local storage and
   * filters the payment methods based on the configured options.
   */
  ngOnInit() {
    // Read the portal settings from local storage
    let portalSettings = JSON.parse(localStorage.getItem('portal_settings'));
    // Get the payment type configuration
    let payment_type = portalSettings?.payment_type?.trustpayment || {};

    // If the key is not set, then the payment type is not configured
    if (!payment_type.key) {
      // Clear the payment methods array
      this.paymentMethods = [];
      // Return early
      return;
    }

    // If the options are set, then filter the payment methods based on the set options
    if (payment_type.options?.length) {
      // Filter the payment methods
      this.paymentMethods = this.paymentMethods.filter((method) =>
        payment_type.options.includes(method.type)
      );
    }
    if (payment_type.option_labels && Object.keys(payment_type.option_labels).length) {
      this.paymentMethods = this.paymentMethods.map(method => ({
        ...method,
        title: payment_type.option_labels[method.type] ?? method.title
      }));
    }
  }

  /**
   * Handles the click event for the payment method.
   *
   * @param method The selected payment method
   */
  handleMethodClick(method: PaymentMethod) {
    const commonParams = {
      amount: this.amount,
      caseId: this.caseId,
      totalOutstandingWithSymbol: this.totalOutstandingWithSymbol,
      debtorDetails: this.debtorDetails,
      caseDetails: this.caseDetails,
      responseData: this.responseData,
    };

    /**
     * Switch case for the payment method type
     *
     * If the payment method is card, then push the TrustPaymentPage
     * with the common parameters.
     *
     * If the payment method is google pay or apple pay, then push the
     * TrustPaymentDigitalWalletPage with the common parameters and the
     * payment method type.
     */
    switch (method.type) {
      case 'card':
        this.ionNav.push(TrustPaymentPage, commonParams);
        break;
      case 'googlepay':
      case 'applepay':
      case 'paybybank':
        this.ionNav.push(TrustPaymentDigitalWalletPage, {
          ...commonParams,
          paymentMethod: method.type,
        });
        break;
    }
  }

  /**
   * Navigates back to the previous page.
   *
   * @returns {void}
   */
  dismiss(): void {
    this.ionNav.pop();
  }
}